import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import React from "react";
import { useDropzone } from "react-dropzone";
import { uploadFileToUploadCare } from "../../attachments/helpers.js";
import { FORM_COLORS, TEXT_BUTTON_SX } from "./onboardingFormStyles.js";
import type { OnboardingLogo } from "./mergePrefillIntoFormValues.js";
import {
  getLogoFileProblem,
  LOGO_ACCEPTED_MIME_TYPES,
  type LogoFileProblem,
} from "./onboardingLogo.js";

interface Props {
  value: OnboardingLogo | null;
  /** The uploaded logo, or `null` once the user removed it. Nothing is saved here. */
  onChange: (logo: OnboardingLogo | null) => void;
  disabled?: boolean;
  /** A logo found on the website is being imported: the field shows it as busy. */
  importing?: boolean;
}

const ACCEPT = LOGO_ACCEPTED_MIME_TYPES.join(",");

/**
 * The company logo of the business step: a full-width dashed dropzone that
 * becomes a box with the logo at 96×96 (scaled to fit, never distorted),
 * Replace and Remove once a logo is set. The logo's file name or address is
 * not shown: a found logo would only show a CDN name. A file is uploaded as it is (no recompression) right
 * after the drop; saving it is up to the form's "Save and continue".
 */
export const OnboardingLogoDropzone = ({
  value,
  onChange,
  disabled = false,
  importing = false,
}: Props) => {
  const { t } = useTranslate(["Global", "OrganisationProfile"]);
  const { t: attachmentT } = useTranslate("AttachmentField");
  const [uploadingFile, setUploading] = React.useState(false);
  const uploading = uploadingFile || importing;
  const [problem, setProblem] = React.useState<
    LogoFileProblem | "upload" | null
  >(null);
  const pickerRef = React.useRef<HTMLInputElement>(null);

  const showProblem = (files: File[]) => {
    const [file] = files;
    setProblem(file ? (getLogoFileProblem(file) ?? "type") : "type");
  };

  const handleFiles = async (files: File[]) => {
    const [file] = files;
    if (!file || uploading) {
      return;
    }
    if (getLogoFileProblem(file)) {
      showProblem(files);
      return;
    }
    setProblem(null);
    setUploading(true);
    try {
      const attachment = await uploadFileToUploadCare(file, false);
      onChange({
        url: attachment.url,
        title: attachment.title,
        mimeType: attachment.mimeType,
      });
    } catch {
      setProblem("upload");
    } finally {
      setUploading(false);
    }
  };

  const problemMessage =
    problem === "size"
      ? t("The file is larger than 5 MB.", { ns: "OrganisationProfile" })
      : problem === "type"
        ? t("Please use a PNG, SVG or JPG file.", { ns: "OrganisationProfile" })
        : problem === "upload"
          ? t("Something went wrong. Please try again.", { ns: "Global" })
          : null;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: files => void handleFiles(files),
    onDropRejected: rejections => showProblem(rejections.map(r => r.file)),
    accept: ACCEPT,
    disabled: disabled || uploading || Boolean(value),
    multiple: false,
  });

  return (
    <Box aria-busy={uploading}>
      {value ? (
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            gap: "16px",
            p: "12px",
            border: `1px solid ${FORM_COLORS.line}`,
            borderRadius: "6px",
            bgcolor: "common.white",
          }}
        >
          <Box
            component="img"
            alt={t("Company logo", { ns: "OrganisationProfile" })}
            src={value.url}
            sx={{
              width: 96,
              height: 96,
              flex: "0 0 auto",
              objectFit: "contain",
              borderRadius: "6px",
            }}
          />
          <Stack sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" sx={{ gap: "6px", ml: "-5px" }}>
              <Button
                variant="text"
                size="small"
                disabled={disabled || uploading}
                onClick={() => pickerRef.current?.click()}
                sx={TEXT_BUTTON_SX}
              >
                {t("Replace", { ns: "OrganisationProfile" })}
              </Button>
              <Button
                variant="text"
                size="small"
                disabled={disabled || uploading}
                onClick={() => {
                  setProblem(null);
                  onChange(null);
                }}
                sx={TEXT_BUTTON_SX}
              >
                {t("Remove", { ns: "Global" })}
              </Button>
            </Stack>
          </Stack>
          {importing && <CircularProgress size={20} aria-hidden={false} />}
          <input
            ref={pickerRef}
            type="file"
            accept={ACCEPT}
            hidden
            onChange={event => {
              void handleFiles(Array.from(event.target.files ?? []));
              // Choosing the same file again must fire a change as well.
              event.target.value = "";
            }}
          />
        </Stack>
      ) : (
        <Stack
          {...getRootProps()}
          sx={{
            width: "100%",
            minHeight: 76,
            boxSizing: "border-box",
            alignItems: "center",
            justifyContent: "center",
            gap: "4px",
            p: "12px",
            border: `1.5px dashed ${FORM_COLORS.link}`,
            borderRadius: "6px",
            bgcolor: isDragActive ? FORM_COLORS.fieldHover : FORM_COLORS.field,
            cursor: disabled || uploading ? "default" : "pointer",
            "&:hover":
              disabled || uploading
                ? undefined
                : {
                    bgcolor: FORM_COLORS.fieldHover,
                    borderColor: FORM_COLORS.navy,
                  },
          }}
        >
          <input {...getInputProps()} />
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              gap: "8px",
              color: FORM_COLORS.link,
            }}
          >
            <AddPhotoAlternateIcon aria-hidden sx={{ fontSize: 22 }} />
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 500,
                lineHeight: "21.75px",
                letterSpacing: ".3px",
                textTransform: "uppercase",
                color: "inherit",
              }}
            >
              {t("Add company logo", { ns: "OrganisationProfile" })}
            </Typography>
          </Stack>
          <Typography
            sx={{
              fontSize: 14,
              lineHeight: "20.3px",
              letterSpacing: "normal",
              color: FORM_COLORS.muted,
            }}
          >
            {uploading
              ? attachmentT("Uploading…")
              : isDragActive
                ? attachmentT("Drop files here")
                : t("or drag & drop here · PNG, SVG or JPG, max. 5 MB", {
                    ns: "OrganisationProfile",
                  })}
          </Typography>
        </Stack>
      )}
      {problemMessage && (
        <FormHelperText error role="alert">
          {problemMessage}
        </FormHelperText>
      )}
    </Box>
  );
};
